import type { AppDispatch, RootState } from "app/store";
import { getAmbientScribeStatus } from "features/medical-scribe/utils";
import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import { stripLeadingClosingTags } from "utils/html-detect.util";

import { API_TAGS, emptySplitApi } from "./empty-split-api";
import type {
  AmbientScribeNote,
  AmbientScribeNoteFilters
} from "./types/ambient-scribe-note.type";
import type {
  AmbientScribeProcessingContext,
  AmbientScribeProcessingContextApi
} from "./types/ambient-scribe-processing-context.type";
import {
  AMBIENT_SCRIBE_TEMPLATE_TYPE,
  type AmbientScribeTemplate,
  type AmbientScribeTemplateCreateInput,
  type AmbientScribeTemplateFilters,
  type AmbientScribeTemplateUpdateInput,
  type GenerateScribeTemplateInput,
  type ScribeTemplateGenerationStatus
} from "./types/ambient-scribe-template.type";
import type {
  AmbientScribe,
  AmbientScribeFeedbackRequest,
  AmbientScribeFeedbackResponse,
  AmbientScribeFilters,
  AmbientScribeInput,
  AmbientScribeMergeDetail,
  AmbientScribeMergeDryRunResponse,
  AmbientScribeMergeInput,
  AmbientScribeMergeResponse,
  AmbientScribeUpdateInput,
  ScribeAudioChunk,
  ScribeAudioExport,
  ScribeRecordingSession
} from "./types/ambient-scribe.type";
import type { ProcessingEvent } from "./types/processing-event.type";
import type { PromptOutputFormat } from "./types/prompts.type";

/** Args for `getProcessingEvents`. */
export type GetProcessingEventsQueryArg = {
  scribeId: number;
  offset?: number;
  limit?: number;
};

/** Paginated DRF-style response from `GET .../processing-events/`. */
export type GetProcessingEventsResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: ProcessingEvent[];
};

/** Raw API payload: DRF page or a bare list (legacy / alternate BE shape). */
export type GetProcessingEventsApiPayload =
  | GetProcessingEventsResponse
  | ProcessingEvent[];

function normalizeProcessingEventsResponse(
  response: GetProcessingEventsApiPayload,
  arg: GetProcessingEventsQueryArg
): GetProcessingEventsResponse {
  if (Array.isArray(response)) {
    const offset = arg.offset ?? 0;
    const limit = arg.limit ?? 25;
    const results = response.slice(offset, offset + limit);
    const loadedThrough = offset + results.length;
    return {
      count: response.length,
      next: loadedThrough < response.length ? "__has_more__" : null,
      previous: offset > 0 ? "__has_prev__" : null,
      results
    };
  }
  return response;
}

function deriveHasTranscriptionFromProcessingContextTranscription(
  transcription: unknown | null
): boolean {
  if (transcription == null || typeof transcription !== "object") {
    return false;
  }
  const channels = (transcription as { channels?: unknown }).channels;
  if (!Array.isArray(channels) || channels.length === 0) {
    return false;
  }
  const channel0 = channels[0];
  if (channel0 == null || typeof channel0 !== "object") {
    return false;
  }
  const alternatives = (channel0 as { alternatives?: unknown }).alternatives;
  if (!Array.isArray(alternatives) || alternatives.length === 0) {
    return false;
  }
  const alt0 = alternatives[0];
  if (alt0 == null || typeof alt0 !== "object") {
    return false;
  }
  const transcript = (alt0 as { transcript?: unknown }).transcript;
  return typeof transcript === "string" && transcript.trim() !== "";
}

function transformAmbientScribeProcessingContextResponse(
  response: AmbientScribeProcessingContextApi
): AmbientScribeProcessingContext {
  const backendStatus = response.status;
  const has_transcription =
    deriveHasTranscriptionFromProcessingContextTranscription(
      response.transcription
    );
  const status = getAmbientScribeStatus({
    id: response.id,
    status: backendStatus,
    has_transcription
  });
  return {
    ...response,
    backend_status: backendStatus,
    has_transcription,
    status
  };
}

type ApiTagType = (typeof API_TAGS)[number];
const AMBIENT_SCRIBE_TEMPLATES_TAG = "AmbientScribeTemplates" as ApiTagType;
const AUDIO_CHUNKS_TAG = "AmbientScribeAudioChunks" as ApiTagType;
const AMBIENT_SCRIBE_NOTES_TAG = "AmbientScribeNotes" as ApiTagType;

/**
 * Tags for a scribe LIST read: the collection tag plus one per row.
 *
 * RTK Query tag matching is asymmetric. `providesTags: ["AmbientScribes"]` registers the entry
 * under id `undefined`, and an invalidation of `{ type: "AmbientScribes", id: 180 }` looks up id
 * `180` only — so it matches the single-scribe read (which provides `{type, id}`) and **silently
 * misses every list read**. Invalidating the plain tag is the direction that works both ways.
 *
 * That mismatch is why the `ambientscribe.note.notification` handler in `hooks/use-notifications.tsx`
 * appeared to refresh scribe state and did not: the chart-note regenerate alert reads
 * `is_generating_soap` off `getAmbientScribes`, whose entry that invalidation never reached.
 *
 * Purely additive — an id-scoped invalidation now reaches these entries too; a plain-tag
 * invalidation behaves exactly as before.
 */
export function provideAmbientScribeListTags(
  scribes: readonly AmbientScribe[] | undefined
): ({ type: "AmbientScribes"; id?: number } | "AmbientScribes")[] {
  return [
    "AmbientScribes",
    ...(scribes ?? []).map((scribe) => ({
      type: "AmbientScribes" as const,
      id: Number(scribe.id)
    }))
  ];
}

/**
 * The generated text a scribe read hands to the UI, with any orphan closing tag the model put at
 * the start removed (scribe 65607's `soap_notes` began `"</p>\n<p>…"`). Done here, once, so the
 * Scribe tab, Copy, Insert Scribe, the content dot, the failure banner and bulk download all see
 * the same string; stripping at each reader let them disagree about whether there is content.
 * Only the two text fields; everything else passes through untouched.
 */
export function withScribeTextNormalized<T extends Partial<AmbientScribe>>(
  scribe: T
): T {
  const { soap_notes: soapNotes, summary } = scribe;
  return {
    ...scribe,
    ...(typeof soapNotes === "string"
      ? { soap_notes: stripLeadingClosingTags(soapNotes) }
      : {}),
    ...(typeof summary === "string"
      ? { summary: stripLeadingClosingTags(summary) }
      : {})
  };
}

/** `transform_type` for the audiology-report-summary branch of `POST /{id}/async/` (doc §6.4). */
export const AUDIOLOGY_REPORT_SUMMARY_TRANSFORM_TYPE =
  "audiology_report_summary";

const AUDIOLOGY_SUMMARY_PROGRESS_FIELD = "is_generating_audiology_summary";

/** `transform_type` for the After Visit Summary letter branch of `POST /{id}/async/`. */
export const AFTER_VISIT_SUMMARY_TRANSFORM_TYPE = "after_visit_summary";

const AFTER_VISIT_SUMMARY_PROGRESS_FIELD = "is_generating_after_visit_summary";
const CONSULT_DEBRIEF_ID_FIELD = "consult_debrief_id";

// SN-19067 template-type projections. Only send these behind
// AMBIENT_SCRIBE_TEMPLATE_TYPES — naming `created_by` / `updated_by` against a backend
// that predates the change fails the whole GET with a 400.
//
// A RestQL inclusion list REPLACES the default payload, so each of these has to re-list
// every field its consumer already renders. Dropping one shows up as a silently blank
// column, not an error.
// NOTE: `appointment_type_id` is deliberately absent — the backend removed that column, and
// naming a missing field here fails the WHOLE read with the bare-array 400. Linkage now comes
// from the `appointment_types` array.
export const AMBIENT_SCRIBE_TEMPLATES_QUERY_WITH_TYPES =
  "{id,name,template,practice_id,is_default,add_to_chart_notes,type,created_at,updated_at,appointment_types{id,name,general_type,color},created_by{id,first_name,last_name,full_name,name,suffix,title},updated_by{id,first_name,last_name,full_name,name,suffix,title}}";

// Narrow projection for the scribe-detail template picker: it renders the name and the type,
// and reads `is_default` to decide whether to offer the built-in-prompt option — a practice that
// already has a default ChartNote template does not need it. Appointment types come from the
// authoritative AppointmentType side (see use-scribe-template-appointment-types.hook.ts), not
// from the template's own reverse FK.
//
// `is_default` is safe to name here: it predates SN-19067 and is already listed in
// AMBIENT_SCRIBE_TEMPLATES_QUERY_WITH_TYPES above, unlike `appointment_types` / `created_by` /
// `updated_by`, which fail the WHOLE read with the bare-array 400 against an older backend.
export const AMBIENT_SCRIBE_TEMPLATE_SELECT_QUERY = "{id,name,type,is_default}";

/**
 * Projection for `GET /ai/ambient-scribes/notes/` — every field the UI reads, minus `prompt`.
 *
 * Same RestQL rule as the template projections above: an inclusion list REPLACES the default
 * payload, so this has to name everything its consumers render. `error` is an object and needs
 * its own sub-selection; dropping a field shows up as silently missing UI, not an error.
 *
 * It names every NON-optional field on `AmbientScribeNote` — including the three FK ids nothing
 * renders today — so the interface stays honest about what a projected read actually contains.
 * `prompt` is the one field declared optional, precisely because it is projection-gated.
 */
export const AMBIENT_SCRIBE_NOTE_QUERY =
  "{id,ambient_scribe_id,ambient_scribe_template_id,patient_id,practice_id,staff_id,notes,parent_type,parent_id,run_type,status,prompt_version,duration_ms,error,created_at,updated_at}";

/**
 * The above plus `prompt`, for the collapsible prompt disclosure in the scribe details dialog.
 *
 * Use this ONLY on a single-row read. `prompt` embeds the entire transcript, so asking for it on
 * a list request multiplies a multi-KB payload by the page size.
 */
export const AMBIENT_SCRIBE_NOTE_QUERY_WITH_PROMPT = `${AMBIENT_SCRIBE_NOTE_QUERY.slice(0, -1)},prompt}`;

/**
 * The note projection, composed from opt-in extras.
 *
 * `sent_email_id` is opt-in rather than added to `AMBIENT_SCRIBE_NOTE_QUERY`, for the same reason
 * `APPOINTMENT_TYPES_QUERY_SCRIBE_TEMPLATE_LINKS_WITH_AVS` exists: the shared projection also
 * serves the audiology-summary tab, and RestQL 400s a whole read when it names a field the backend
 * does not have. Widening it would break audiology against a backend predating this column — a
 * regression in a feature that has nothing to do with letters. Only the flag-gated After Visit
 * Summary surfaces ask for it, so backend deploys first and nothing else is exposed.
 *
 * Composed rather than enumerated because the scribe-details letter tab wants BOTH `prompt` (its
 * prompt disclosure) and `sent_email_id`, which a fixed set of constants cannot express.
 */
export const buildAmbientScribeNoteQuery = ({
  withPrompt = false,
  withSentEmail = false
}: { withPrompt?: boolean; withSentEmail?: boolean } = {}) => {
  const extras = [
    ...(withSentEmail ? ["sent_email_id"] : []),
    // Last on purpose: `AMBIENT_SCRIBE_NOTE_QUERY_WITH_PROMPT` ends in `,prompt}` and a test pins
    // that shape, so anything appended after it would change a projection other callers rely on.
    ...(withPrompt ? ["prompt"] : [])
  ];
  if (!extras.length) return AMBIENT_SCRIBE_NOTE_QUERY;
  return `${AMBIENT_SCRIBE_NOTE_QUERY.slice(0, -1)},${extras.join(",")}}`;
};

/**
 * Params for the appointment-type form's scribe-template dropdown.
 *
 * `AppointmentType.ambient_scribe_template_id` is what the scribe-time resolver reads to
 * generate the CHART NOTE, and POST /{id}/async/ with a non-ChartNote template writes
 * nothing at all (doc §6.2). An AudiologyReport template assigned here would leave the
 * appointment type looking configured while silently generating nothing — so once types
 * exist, only ChartNote templates may be offered.
 *
 * The prefetch in `edit-appointment-type.component.tsx` and the form's own query MUST pass
 * the same object, or they land in different RTK cache entries and the dropdown renders
 * before its pre-selected value is available.
 */
export function getAppointmentTypeScribeTemplateParams(
  isTemplateTypesEnabled: boolean
): AmbientScribeTemplateFilters {
  return isTemplateTypesEnabled
    ? { limit: 100, type: AMBIENT_SCRIBE_TEMPLATE_TYPE.CHART_NOTE }
    : { limit: 100 };
}

/**
 * The appointment-type form's After Visit Summary slot offers letter templates only. One params
 * builder so the form and the edit page's cache-warming prefetch share a single cache entry.
 */
export function getAfterVisitSummaryTemplateParams(): AmbientScribeTemplateFilters {
  return { limit: 100, type: AMBIENT_SCRIBE_TEMPLATE_TYPE.AFTER_VISIT_SUMMARY };
}

/**
 * Chart-note ↔ scribe links for the patient chart-notes screen.
 *
 * Deliberately minimal — the no-`?query=` call on this endpoint costs ~1486 queries / 800KB
 * at 20 rows. `status` is not rendered anywhere but is load-bearing: `getAmbientScribes`
 * runs every row through `getAmbientScribeStatus` in `transformResponse`, which tolerates a
 * missing `status` without throwing and would therefore derive a silently wrong value for
 * any later reader.
 */
export const AMBIENT_SCRIBES_QUERY_CHART_NOTE_LINKS =
  "{id,status,chart_note{id},ambient_scribe_template_id,is_generating_soap,created_at}";

/**
 * What the merge dialog needs per selected scribe (SN-19901). `chart_note_id` is the flat scalar
 * (`chart_note{id}` would serialize the whole note), and every field already exists on the backend,
 * so this read is safe before and after the merge endpoint ships.
 */
export const MERGE_SCRIBE_QUERY =
  "{id,status,created_at,duration,session_count,chart_note_id,ambient_scribe_template{id,name},clinic{id,name,timezone},staff{id,first_name,last_name,title,suffix}}";

export const DEFAULT_AMBIENT_SCRIBES_QUERY_NON_STREAMING =
  "{id,created_at,updated_at,duration,has_transcription,recording,patient{id,first_name,middle_name,last_name,preferred_name,title,suffix},staff{id,first_name,last_name,suffix,title,is_active}}";

export const DEFAULT_AMBIENT_SCRIBES_QUERY_DETAILED_NON_STREAMING =
  "{id,created_at,updated_at,duration,has_transcription,recording,patient{id,first_name,middle_name,last_name,preferred_name,title,suffix},staff{id,first_name,last_name,suffix,title,is_active},summary,soap_notes,transcription}";

export const DEFAULT_AMBIENT_SCRIBES_QUERY =
  "{id,created_at,updated_at,status,duration,is_generating_summary,is_generating_soap,has_transcription,recording,patient{id,first_name,middle_name,last_name,preferred_name,title,suffix},staff{id,first_name,last_name,suffix,title,is_active}}";

export const DEFAULT_AMBIENT_SCRIBES_QUERY_DETAILED =
  "{id,created_at,updated_at,status,duration,is_generating_summary,is_generating_soap,has_transcription,recording,patient{id,first_name,middle_name,last_name,preferred_name,title,suffix},staff{id,first_name,last_name,suffix,title,is_active},summary,soap_notes,transcription}";

export const DEFAULT_AMBIENT_SCRIBE_QUERY_NON_STREAMING =
  "{id,summary,soap_notes,has_transcription,transcription,created_at,updated_at,duration,recording,patient{id,first_name,middle_name,last_name,preferred_name,title,suffix},staff{id,first_name,last_name,suffix,title,is_active}}";

export const DEFAULT_AMBIENT_SCRIBE_QUERY =
  "{id,summary,soap_notes,has_transcription,transcription,is_generating_summary,is_generating_soap,status,created_at,updated_at,duration,recording,custom_context,patient{id,first_name,middle_name,last_name,preferred_name,title,suffix},staff{id,first_name,last_name,suffix,title,is_active},clinic{id,name,timezone}}";

export const DEFAULT_AMBIENT_SCRIBE_QUERY_WITH_TEMPLATE =
  "{id,summary,soap_notes,has_transcription,transcription,is_generating_summary,is_generating_soap,status,created_at,updated_at,duration,recording,custom_context,patient{id,first_name,middle_name,last_name,preferred_name,title,suffix},staff{id,first_name,last_name,suffix,title,is_active},clinic{id,name,timezone},ambient_scribe_template_id}";

// Paused scribe edit (SN-17727): adds custom_context + chart_note{id} (id only) for the
// Edit Scribe tab and resume-metadata. Scoped to the paused-edit UI so other callers'
// payloads are unchanged.
export const DEFAULT_AMBIENT_SCRIBE_QUERY_PAUSED =
  "{id,summary,soap_notes,has_transcription,transcription,is_generating_summary,is_generating_soap,status,created_at,updated_at,duration,recording,custom_context,chart_note{id},ambient_scribe_template_id,patient{id,first_name,middle_name,last_name,preferred_name,title,suffix},staff{id,first_name,last_name,suffix,title,is_active},clinic{id,name,timezone}}";

/**
 * Adds `is_generating_audiology_summary` to any of the detail projections above (SN-19067).
 *
 * ONLY call this behind AMBIENT_SCRIBE_TEMPLATE_TYPES. Naming the field against a backend that
 * predates SN-19067 fails the WHOLE read with the bare-array 400, which would break the scribe
 * detail pane outright — not just the audiology tab.
 *
 * A helper rather than three more `DEFAULT_AMBIENT_SCRIBE_QUERY*` constants: those are ~400
 * characters each and every consumer re-lists every field it renders, so a copy that drifts
 * shows up as a silently blank field rather than an error.
 */
/**
 * Fields the Scribes list screen's appointment-type / template / chart-note columns need.
 *
 * `chart_note_id` is a flat scalar rather than `chart_note{id}` on purpose: the nested form makes
 * the backend serialize a full ChartNoteSerializer — `ChartNote.text`, the whole note body — into
 * every row of the page.
 */
export const SCRIBE_LIST_COLUMN_FIELDS =
  "appointment_type,chart_note_id,ambient_scribe_template{id,name}";

/**
 * Adds the Scribes list column fields to any of the list projections above.
 *
 * ONLY call this behind the `AMBIENT_SCRIBE_LIST_FILTERS` LD flag. `appointment_type` and
 * `chart_note_id` do not exist on a backend that predates this change, and naming an unknown
 * field fails the WHOLE read with a 400 (the same RestQL failure mode documented on
 * `withAudiologySummaryProgress` and `withConsultDebriefId`) — which on this screen means a blank
 * list, not a missing column.
 */
export function withScribeListColumns(query: string): string {
  // Idempotent — callers derive the base projection from flag combinations, so double-wrapping is
  // easy to introduce and would send the fields twice.
  if (query.includes("appointment_type")) return query;
  if (!query.endsWith("}")) return query;
  return `${query.slice(0, -1)},${SCRIBE_LIST_COLUMN_FIELDS}}`;
}

export function withAudiologySummaryProgress(query: string): string {
  // Idempotent — callers derive the base projection from flag combinations, so double-wrapping
  // is easy to introduce and would send the field twice.
  if (query.includes(AUDIOLOGY_SUMMARY_PROGRESS_FIELD)) return query;
  if (!query.endsWith("}")) return query;
  return `${query.slice(0, -1)},${AUDIOLOGY_SUMMARY_PROGRESS_FIELD}}`;
}

/**
 * Adds `consult_debrief_id` to any of the detail projections above (ENG-1706).
 *
 * ONLY call this behind the `consultCoachSignals` LD flag. Naming the field against a backend
 * that predates ENG-1706 fails the WHOLE read with the bare-array 400 (same RestQL failure mode
 * documented on `withAudiologySummaryProgress`), which would break the scribe detail pane
 * outright — not just the Debrief tab.
 */
export function withConsultDebriefId(query: string): string {
  // Idempotent — callers derive the base projection from flag combinations, so double-wrapping
  // is easy to introduce and would send the field twice.
  if (query.includes(CONSULT_DEBRIEF_ID_FIELD)) return query;
  if (!query.endsWith("}")) return query;
  return `${query.slice(0, -1)},${CONSULT_DEBRIEF_ID_FIELD}}`;
}

/**
 * Adds `is_generating_after_visit_summary` to a detail projection.
 *
 * ONLY call this behind the `afterVisitSummary` LD flag: naming the field against a backend that
 * predates the feature fails the WHOLE scribe read with the bare-array 400 (the RestQL failure mode
 * documented on `withAudiologySummaryProgress`).
 */
export function withAfterVisitSummaryProgress(query: string): string {
  // Idempotent — callers derive the base projection from flag combinations, so double-wrapping
  // is easy to introduce and would send the field twice.
  if (query.includes(AFTER_VISIT_SUMMARY_PROGRESS_FIELD)) return query;
  if (!query.endsWith("}")) return query;
  return `${query.slice(0, -1)},${AFTER_VISIT_SUMMARY_PROGRESS_FIELD}}`;
}

type AmbientScribeDetailCachePatch = Partial<
  Pick<AmbientScribe, "custom_context" | "updated_at">
>;

/** Patches all cached getAmbientScribe entries for a scribe id (SN-18681). */
export function patchAmbientScribeDetailCustomContext(
  dispatch: AppDispatch,
  getState: () => RootState,
  id: number,
  patch: AmbientScribeDetailCachePatch
) {
  if (patch.custom_context === undefined && patch.updated_at === undefined) {
    return;
  }
  const cachedArgs = ambientScribeApi.util.selectCachedArgsForQuery(
    getState(),
    "getAmbientScribe"
  );
  for (const originalArgs of cachedArgs) {
    if (originalArgs.id !== id) continue;
    dispatch(
      ambientScribeApi.util.updateQueryData(
        "getAmbientScribe",
        originalArgs,
        (draft) => {
          Object.assign(draft, patch);
        }
      )
    );
  }
}

export const ambientScribeApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getAmbientScribe: builder.query<
      AmbientScribe,
      { id: number; query?: string }
    >({
      query: ({ id, query = DEFAULT_AMBIENT_SCRIBE_QUERY }) => ({
        url: `/ai/ambient-scribes/${id}/?query=${query}`
      }),
      transformResponse: (response: AmbientScribe) => {
        return withScribeTextNormalized({
          ...response,
          status: getAmbientScribeStatus(response)
        });
      },
      providesTags: (result, error, arg) => [
        { type: "AmbientScribes", id: arg.id }
      ]
    }),

    /**
     * Raw backend status for one scribe, deliberately untransformed.
     *
     * getAmbientScribe cannot answer this question: its transformResponse
     * overwrites `status` with getAmbientScribeStatus(), which uses a different
     * numbering (CANCELLED is 7 on the backend and 10 here) and collapses a
     * null status to UPLOADED. A scribe whose status rows do not exist yet would
     * therefore read as unresumable, and the reload guard would discard a live
     * session. Callers here compare against AMBIENT_SCRIBE_BACKEND_STATUS_*.
     *
     * Untagged and not cached: it is a point-in-time probe on page load, and a
     * stale answer either way would defeat the point.
     */
    getAmbientScribeBackendStatus: builder.query<
      { id: number; status: number | null },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/ai/ambient-scribes/${id}/?query={id,status}`
      }),
      keepUnusedDataFor: 0
    }),

    getAmbientScribes: builder.query<AmbientScribe[], AmbientScribeFilters>({
      query: ({
        offset,
        limit = 25,
        query = DEFAULT_AMBIENT_SCRIBES_QUERY,
        ...params
      }) => {
        return {
          url: `/ai/ambient-scribes/?offset=${
            offset || 0
          }&limit=${limit}&query=${query}&${formatValidQueryParamUrlCommaSeparated(
            params
          )}`,
          method: "GET"
        };
      },
      providesTags: provideAmbientScribeListTags,
      transformResponse: (
        response:
          | AmbientScribe[]
          | {
              count: number;
              next: string;
              previous: string;
              results: AmbientScribe[];
            }
      ) => {
        let result: AmbientScribe[] = [];
        if ("count" in response) {
          result = response.results;
        } else {
          result = response;
        }
        return result.map((scribe) =>
          withScribeTextNormalized({
            ...scribe,
            status: getAmbientScribeStatus(scribe)
          })
        );
      }
    }),

    getAmbientScribesPaginated: builder.query<
      { results: AmbientScribe[]; count: number },
      AmbientScribeFilters & { offset: number; limit: number }
    >({
      query: ({
        offset = 0,
        limit = 25,
        query = DEFAULT_AMBIENT_SCRIBES_QUERY,
        ...params
      }) => ({
        url: `/ai/ambient-scribes/?offset=${offset}&limit=${limit}&query=${query}&${formatValidQueryParamUrlCommaSeparated(
          params
        )}`,
        method: "GET"
      }),
      providesTags: (result) => provideAmbientScribeListTags(result?.results),
      transformResponse: (
        response:
          | AmbientScribe[]
          | {
              count: number;
              next: string;
              previous: string;
              results: AmbientScribe[];
            }
      ) => {
        let results: AmbientScribe[] = [];
        let count = 0;
        if ("count" in response) {
          results = response.results;
          count = response.count;
        } else {
          results = Array.isArray(response) ? response : [];
          count = results.length;
        }
        return {
          results: results.map((scribe) => ({
            ...scribe,
            status: getAmbientScribeStatus(scribe)
          })),
          count
        };
      }
    }),

    createAmbientScribe: builder.mutation<AmbientScribe, AmbientScribeInput>({
      query: ({ patient, duration, staff, practice, audio_file, clinic }) => {
        const formData = new FormData();
        formData.append("patient", String(patient));
        formData.append("duration", String(duration));
        formData.append("staff", String(staff));
        formData.append("practice", String(practice));
        if (clinic) {
          formData.append("clinic", String(clinic));
        }
        formData.append("audio_file", audio_file);
        formData.append("auto_transcribe", "true");

        return {
          url: "/audio-transcription/",
          method: "POST",
          body: formData
        };
      },
      invalidatesTags: ["AmbientScribes", "AmbientScribeProcessingEvents"]
    }),

    updateAmbientScribe: builder.mutation<
      AmbientScribe,
      AmbientScribeUpdateInput
    >({
      query: ({ id, skipCacheInvalidation, ...body }) => {
        return {
          url: `/ai/ambient-scribes/${id}/`,
          method: "PATCH",
          body
        };
      },
      invalidatesTags: (result, error, arg) => {
        if (error || arg.skipCacheInvalidation) return [];
        return [
          { type: "AmbientScribes", id: arg.id },
          "AmbientScribes",
          "AmbientScribeProcessingEvents"
        ];
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        // skipCacheInvalidation: autosave patches getAmbientScribe detail caches only —
        // avoids list refetch churn while typing. List views refresh on explicit actions
        // (complete, pause, notifyScribeRefetch, etc.).
        if (!arg.skipCacheInvalidation) return;

        try {
          const { data } = await queryFulfilled;
          const cachedArgs = ambientScribeApi.util.selectCachedArgsForQuery(
            getState(),
            "getAmbientScribe"
          );
          const patch: Partial<AmbientScribe> = {};
          if (arg.custom_context !== undefined) {
            patch.custom_context =
              data.custom_context ?? arg.custom_context ?? null;
          }
          if (data.updated_at) {
            patch.updated_at = data.updated_at;
          }
          if (Object.keys(patch).length === 0) return;

          for (const originalArgs of cachedArgs) {
            if (originalArgs.id !== arg.id) continue;
            dispatch(
              ambientScribeApi.util.updateQueryData(
                "getAmbientScribe",
                originalArgs,
                (draft) => {
                  Object.assign(draft, patch);
                }
              )
            );
          }
        } catch {
          // no-op — failed PATCH should not patch cache
        }
      }
    }),

    generateAmbientSummary: builder.mutation<AmbientScribe, { id: number }>({
      query: ({ id }) => {
        return {
          url: `/ai/ambient-scribes/${id}/transform/`,
          method: "POST",
          body: {
            transform_type: "summary"
          }
        };
      },
      invalidatesTags: ["AmbientScribes", "AmbientScribeProcessingEvents"]
    }),
    generateAmbientSoapNotes: builder.mutation<AmbientScribe, { id: number }>({
      query: ({ id }) => {
        return {
          url: `/ai/ambient-scribes/${id}/transform/`,
          method: "POST",
          body: {
            transform_type: "soap"
          }
        };
      },
      invalidatesTags: ["AmbientScribes", "AmbientScribeProcessingEvents"]
    }),

    /**
     * `chart_note_id` names the chart note the regenerated SOAP output should land on, instead
     * of leaving the backend to resolve it from the scribe's own link.
     *
     * `add_soap_to_chart_note` overrides the template's own `add_to_chart_notes` flag for this one
     * request (the ask in `docs/ambient-scribes/backend-gaps-sn-19067.md` §5, now shipped). Send it
     * from surfaces where the user's intent is explicitly "update THIS note" — without it, picking
     * a template configured not to write chart notes makes the button look like it worked while
     * nothing visibly changes.
     *
     * Both optional, and callers must only pass them behind AMBIENT_SCRIBE_TEMPLATE_TYPES:
     * `/async/` is on a clinical critical path, so a flag-off deploy has to keep this body
     * byte-identical.
     */
    generateAmbientScribeAsync: builder.mutation<
      void,
      {
        scribe_id: number;
        ambient_scribe_template_id?: number;
        chart_note_id?: number;
        add_soap_to_chart_note?: boolean;
      }
    >({
      query: ({
        scribe_id,
        ambient_scribe_template_id,
        chart_note_id,
        add_soap_to_chart_note
      }) => {
        const body: {
          transform_type: "soap";
          format: "html";
          ambient_scribe_template_id?: number;
          chart_note_id?: number;
          add_soap_to_chart_note?: boolean;
        } = {
          transform_type: "soap",
          format: "html"
        };
        if (ambient_scribe_template_id != null) {
          body.ambient_scribe_template_id = ambient_scribe_template_id;
        }
        if (chart_note_id != null) {
          body.chart_note_id = chart_note_id;
        }
        if (add_soap_to_chart_note != null) {
          body.add_soap_to_chart_note = add_soap_to_chart_note;
        }
        return {
          url: `/ai/ambient-scribes/${scribe_id}/async/`,
          method: "POST",
          body
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "AmbientScribes", id: arg.scribe_id },
        "AmbientScribes",
        "AmbientScribeProcessingEvents"
      ]
    }),

    /**
     * Audiology-report-summary branch of `POST /{id}/async/` (SN-19067, doc §6.4).
     *
     * Kept separate from `generateAmbientScribeAsync` — that one hardcodes
     * `transform_type: "soap"` and serves the untouched chart-note path, so a shared endpoint
     * would put the flag-gated body on the SOAP request too.
     *
     * BOTH ids are optional: with no template the backend uses a built-in
     * History/Results/Recommendations prompt, and with no `report_id` it picks the report
     * itself (same clinic day, else nearest within ±7 days).
     *
     * Nothing is written to the scribe except the `is_generating_audiology_summary` flag — the
     * note lands in `getAmbientScribeNotes`. `AmbientScribeNotes` is deliberately NOT
     * invalidated here: no row exists yet, and the flag flipping back to `false` is what tells
     * the UI to refetch.
     */
    generateAudiologyReportSummary: builder.mutation<
      { success: boolean; task_id: string; message: string },
      {
        scribe_id: number;
        ambient_scribe_template_id?: number;
        report_id?: number;
      }
    >({
      query: ({ scribe_id, ambient_scribe_template_id, report_id }) => {
        // Only documented keys — `format` is accepted by the SOAP branch but is not part of
        // this one's contract.
        const body: {
          transform_type: typeof AUDIOLOGY_REPORT_SUMMARY_TRANSFORM_TYPE;
          ambient_scribe_template_id?: number;
          report_id?: number;
        } = { transform_type: AUDIOLOGY_REPORT_SUMMARY_TRANSFORM_TYPE };
        if (ambient_scribe_template_id != null) {
          body.ambient_scribe_template_id = ambient_scribe_template_id;
        }
        if (report_id != null) {
          body.report_id = report_id;
        }
        return {
          url: `/ai/ambient-scribes/${scribe_id}/async/`,
          method: "POST",
          body
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "AmbientScribes", id: arg.scribe_id },
        "AmbientScribeProcessingEvents"
      ]
    }),

    /**
     * Kick off an After Visit Summary letter generation (`transform_type: "after_visit_summary"`).
     *
     * The template id is optional — absent means the built-in Suno letter prompt. Nothing is
     * written to the scribe except `is_generating_after_visit_summary`; the letter lands in
     * `getAmbientScribeNotes` under `parent_type: "AfterVisitSummary"`. `AmbientScribeNotes` is
     * deliberately NOT invalidated here: no row exists yet, and the flag flipping back to `false`
     * is what tells the UI to refetch (same contract as the audiology summary).
     */
    generateAfterVisitSummary: builder.mutation<
      { success: boolean; task_id: string; message: string },
      {
        scribe_id: number;
        ambient_scribe_template_id?: number;
      }
    >({
      query: ({ scribe_id, ambient_scribe_template_id }) => {
        const body: {
          transform_type: typeof AFTER_VISIT_SUMMARY_TRANSFORM_TYPE;
          ambient_scribe_template_id?: number;
        } = { transform_type: AFTER_VISIT_SUMMARY_TRANSFORM_TYPE };
        if (ambient_scribe_template_id != null) {
          body.ambient_scribe_template_id = ambient_scribe_template_id;
        }
        return {
          url: `/ai/ambient-scribes/${scribe_id}/async/`,
          method: "POST",
          body
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "AmbientScribes", id: arg.scribe_id },
        "AmbientScribeProcessingEvents"
      ]
    }),

    /**
     * Generated notes (SN-19067, doc §7). Read apart from one narrow write — see
     * `updateAmbientScribeNote` below, which PATCHes `notes` on an After Visit Summary row.
     * POST, PUT and DELETE return 405.
     *
     * Unlike `GET /templates/` this endpoint is always ENVELOPED, and its default page size is
     * 5 — send an explicit `limit`.
     *
     * Always send a `query` projection (`AMBIENT_SCRIBE_NOTE_QUERY*`) so the payload is explicit
     * about whether it carries `prompt` — that field embeds the whole transcript and must only be
     * requested on a single-row read. See
     * `features/medical-scribe/hooks/use-latest-scribe-note.hook.ts` for the canonical
     * single-artifact read.
     */
    getAmbientScribeNotes: builder.query<
      { results: AmbientScribeNote[]; count: number },
      AmbientScribeNoteFilters
    >({
      query: (filters) => {
        const params: Record<string, string | number | undefined> = {
          ...filters,
          limit: filters.limit ?? 20,
          offset: filters.offset ?? 0,
          ordering: filters.ordering ?? "-pk"
        };
        const queryString = formatValidQueryParamUrlCommaSeparated(params);
        return {
          url: `/ai/ambient-scribes/notes/${queryString ? `?${queryString}` : ""}`,
          method: "GET"
        };
      },
      providesTags: [AMBIENT_SCRIBE_NOTES_TAG],
      // Documented as always enveloped, but normalized the same way as
      // `getAmbientScribeTemplates` so a bare array can never reach a `.results` read.
      transformResponse: (
        response:
          | AmbientScribeNote[]
          | { results?: AmbientScribeNote[]; count?: number }
      ) => {
        if (Array.isArray(response)) {
          return { results: response, count: response.length };
        }
        const results = response.results ?? [];
        return { results, count: response.count ?? results.length };
      }
    }),

    /**
     * A staff correction to an After Visit Summary letter — the only write on the notes endpoint.
     *
     * The letter is reviewed before it is emailed, and until now that review could only be applied
     * in the compose body, so the fix lived on the sent `Email` and never on the note: reopening
     * the dialog showed the uncorrected text, and copy/print still emitted it.
     *
     * The backend accepts `notes` and nothing else, and only on a succeeded `AfterVisitSummary`
     * row (400 otherwise, 400 on a blank body). Response is `{id, notes}` — the update serializer's
     * whole field set, NOT an `AmbientScribeNote`, so do not widen this type without widening that
     * serializer.
     *
     * `AMBIENT_SCRIBE_NOTES_TAG` on its own is enough: every AVS surface reads `notes` off
     * `getAmbientScribeNotes`, so one invalidation refreshes the dialog, the scribe tab, the
     * appointment card and the chart-note rollup. Nothing here changes the scribe, so the
     * `AmbientScribes` tags are deliberately untouched.
     *
     * Callers must not act on the letter until the resulting refetch lands — an Email or Print
     * fired in that window sends the PRE-edit body. `useLatestScribeNote` exposes `isFetching`
     * for exactly that gate.
     */
    updateAmbientScribeNote: builder.mutation<
      { id: number; notes: string },
      { id: number; notes: string }
    >({
      query: ({ id, notes }) => ({
        url: `/ai/ambient-scribes/notes/${id}/`,
        method: "PATCH",
        body: { notes }
      }),
      invalidatesTags: [AMBIENT_SCRIBE_NOTES_TAG]
    }),

    /**
     * Triggers debrief generation for a scribe with no debrief yet (ENG-1706). Wraps the
     * existing, unmodified `enqueue_debrief_for_scribe` pipeline entry point server-side — this
     * is not a new generation path.
     *
     * No `invalidatesTags` — a `202` doesn't mean the debrief exists yet (it's enqueued, not
     * generated), so there's nothing useful to refetch on success. The caller polls
     * `consult_debrief_id` (via the existing scribe-detail poll) until it appears.
     */
    // Success is a bare 202 with no response body — do not type this as a JSON payload
    // (see debrief-tab.component.tsx: reading a field off `data` here crashed on every
    // real success because `data` is `undefined`, not `{enqueued: true}`).
    generateDebrief: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/ai/ambient-scribes/${id}/generate-debrief/`,
        method: "POST"
      })
    }),

    generateTranscription: builder.mutation<AmbientScribe, { id: number }>({
      query: ({ id }) => {
        return {
          url: `/ai/ambient-scribes/${id}/transform/`,
          method: "POST",
          body: {
            transform_type: "transcribe"
          }
        };
      },
      invalidatesTags: ["AmbientScribes", "AmbientScribeProcessingEvents"]
    }),

    deleteAmbientScribe: builder.mutation<AmbientScribe, number>({
      query: (id) => {
        return {
          url: `/ai/ambient-scribes/${id}/`,
          method: "DELETE"
        };
      },
      invalidatesTags: ["AmbientScribes", "AmbientScribeProcessingEvents"]
    }),

    /**
     * The scribes a merge dialog was opened with (SN-19901), one detail read per id — the list
     * endpoint has no id filter. Statuses are FE-mapped like `getAmbientScribe`: backend CANCELLED
     * is 7, which is FE COMPLETED, so a raw status must never reach the merge rules.
     */
    getAmbientScribesForMerge: builder.query<AmbientScribeMergeDetail[], number[]>(
      {
        async queryFn(ids, _api, _extra, baseQuery) {
          const results = await Promise.all(
            ids.map((id) =>
              baseQuery(`/ai/ambient-scribes/${id}/?query=${MERGE_SCRIBE_QUERY}`)
            )
          );
          const failed = results.find((result) => result.error);
          if (failed?.error) return { error: failed.error };
          return {
            data: results.map((result) => {
              const scribe = result.data as AmbientScribeMergeDetail;
              return { ...scribe, status: getAmbientScribeStatus(scribe) };
            })
          };
        },
        keepUnusedDataFor: 0,
        // The dialog shows its own error for each of these; no second, global toast.
        extraOptions: { suppressGlobalErrorToast: true }
      }
    ),

    /** `?dry_run=true`: every merge check, nothing written. A 200 carries warnings and any block. */
    checkAmbientScribesMerge: builder.mutation<
      AmbientScribeMergeDryRunResponse,
      { scribe_ids: number[] }
    >({
      query: (body) => ({
        url: "/ai/ambient-scribes/merge/?dry_run=true",
        method: "POST",
        body
      }),
      extraOptions: { suppressGlobalErrorToast: true }
    }),

    mergeAmbientScribes: builder.mutation<
      AmbientScribeMergeResponse,
      AmbientScribeMergeInput
    >({
      query: (body) => ({
        url: "/ai/ambient-scribes/merge/",
        method: "POST",
        body
      }),
      extraOptions: { suppressGlobalErrorToast: true },
      // The merged scribe's sessions, events and action items change, and the sources disappear.
      // Its note and letter are rewritten later, by the pipeline; the "Scribe generated"
      // notification refreshes those.
      invalidatesTags: [
        "AmbientScribes",
        "AmbientScribeProcessingEvents",
        "AmbientScribeSessions",
        AUDIO_CHUNKS_TAG,
        "FollowUps",
        "FollowUpGenerations",
        "ScribeInsights"
      ]
    }),

    /**
     * After a merge request died without an answer (a 502, a dropped connection): which of the
     * selected scribes still exist. `true` = 200, `false` = 404, `null` = could not tell.
     */
    probeAmbientScribesExist: builder.mutation<
      { id: number; exists: boolean | null }[],
      number[]
    >({
      async queryFn(ids, _api, _extra, baseQuery) {
        const results = await Promise.all(
          ids.map((id) => baseQuery(`/ai/ambient-scribes/${id}/?query={id}`))
        );
        return {
          data: results.map((result, index) => ({
            id: ids[index],
            exists: result.error
              ? result.error.status === 404
                ? false
                : null
              : true
          }))
        };
      },
      extraOptions: { suppressGlobalErrorToast: true }
    }),

    getAmbientScribeFeedback: builder.query<
      AmbientScribeFeedbackResponse[],
      number
    >({
      query: (id) => ({
        url: `/ai/ambient-scribes/${id}/feedback/`
      }),
      providesTags: ["AmbientScribeFeedback"]
    }),

    submitAmbientScribeFeedback: builder.mutation<
      AmbientScribeFeedbackResponse,
      AmbientScribeFeedbackRequest & { scribe_id: number }
    >({
      query: ({ scribe_id, ...body }) => {
        return {
          url: `/ai/ambient-scribes/${scribe_id}/feedback/`,
          method: "POST",
          body: {
            ...body
          }
        };
      },
      invalidatesTags: [
        "AmbientScribeFeedback",
        "AmbientScribeProcessingEvents"
      ]
    }),

    finalizeStuckScribe: builder.mutation<void, { scribe_id: number }>({
      query: ({ scribe_id }) => ({
        url: `/ai/ambient-scribes/${scribe_id}/finalize/`,
        method: "POST"
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "AmbientScribes", id: arg.scribe_id },
        "AmbientScribes",
        "AmbientScribeProcessingEvents",
        "AmbientScribeSessions"
      ]
    }),

    extractInsights: builder.mutation<{ task_id: string }, number>({
      query: (scribeId) => ({
        url: `/ai/ambient-scribes/${scribeId}/extract-insights/`,
        method: "POST"
      }),
      invalidatesTags: ["ScribeInsights", "AmbientScribeProcessingEvents"]
    }),

    // Mark an already session-paused scribe as Paused status (resumable, not finalized).
    // BE route: POST .../pause/ (SN-17727). FE tears down widget after 2xx.
    pauseAmbientScribe: builder.mutation<
      AmbientScribe,
      { scribe_id: number; duration?: number }
    >({
      query: ({ scribe_id, duration }) => ({
        url: `/ai/ambient-scribes/${scribe_id}/pause/`,
        method: "POST",
        body: duration != null ? { duration } : {}
      }),
      transformResponse: (response: AmbientScribe) => ({
        ...response,
        status: getAmbientScribeStatus(response)
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "AmbientScribes", id: arg.scribe_id },
        "AmbientScribes"
      ]
    }),

    // Merge linked chart-note content into custom_context (SN-18681).
    syncAmbientScribeContext: builder.mutation<AmbientScribe, { id: number }>({
      query: ({ id }) => ({
        url: `/ai/ambient-scribes/${id}/sync-context/`,
        method: "POST",
        body: {}
      }),
      transformResponse: (response: AmbientScribe) => ({
        ...response,
        status: getAmbientScribeStatus(response)
      }),
      invalidatesTags: []
    }),

    getScribeRecordingSessions: builder.query<
      ScribeRecordingSession[],
      { scribeId: number }
    >({
      query: ({ scribeId }) => ({
        url: `/ai/ambient-scribes/${scribeId}/recording-sessions/`
      }),
      transformResponse: (
        response:
          | ScribeRecordingSession[]
          | { results: ScribeRecordingSession[] }
      ) => (Array.isArray(response) ? response : (response.results ?? [])),
      providesTags: (result, error, arg) => [
        "AmbientScribeSessions",
        { type: "AmbientScribes", id: arg.scribeId }
      ]
    }),

    deleteScribeRecordingSession: builder.mutation<
      void,
      { scribeId: number; sessionId: number }
    >({
      query: ({ scribeId, sessionId }) => ({
        url: `/ai/ambient-scribes/${scribeId}/recording-sessions/${sessionId}/`,
        method: "DELETE"
      }),
      invalidatesTags: (result, error, arg) => [
        "AmbientScribeSessions",
        { type: "AmbientScribes", id: arg.scribeId },
        "AmbientScribeProcessingEvents"
      ]
    }),

    // Revert a COMPLETED/FAILED/CANCELLED scribe to Paused so audio chunks
    // can be deleted and the scribe re-completed (SN-17727).
    editAmbientScribe: builder.mutation<AmbientScribe, { scribe_id: number }>({
      query: ({ scribe_id }) => ({
        url: `/ai/ambient-scribes/${scribe_id}/edit/`,
        method: "POST"
      }),
      transformResponse: (response: AmbientScribe) => ({
        ...response,
        status: getAmbientScribeStatus(response)
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "AmbientScribes", id: arg.scribe_id },
        "AmbientScribes",
        AUDIO_CHUNKS_TAG
      ]
    }),

    // Trigger full transcription → SOAP pipeline for a Paused scribe (SN-17727).
    completeAmbientScribe: builder.mutation<
      {
        success: boolean;
        message?: string;
        already_complete?: boolean;
        cancelled?: boolean;
        reason?: string;
        status?: number;
      },
      { scribe_id: number }
    >({
      query: ({ scribe_id }) => ({
        url: `/ai/ambient-scribes/${scribe_id}/complete/`,
        method: "POST"
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "AmbientScribes", id: arg.scribe_id },
        "AmbientScribes",
        "AmbientScribeSessions",
        AUDIO_CHUNKS_TAG
      ]
    }),

    // List audio chunks for a scribe — richer than recording-sessions: includes
    // presigned audio URL and per-chunk transcription_chunks (SN-17727).
    getScribeAudioChunks: builder.query<
      ScribeAudioChunk[],
      { scribeId: number }
    >({
      query: ({ scribeId }) => ({
        url: `/ai/ambient-scribes/${scribeId}/audio-chunks/`
      }),
      transformResponse: (
        response: ScribeAudioChunk[] | { results: ScribeAudioChunk[] }
      ) => (Array.isArray(response) ? response : (response.results ?? [])),
      providesTags: (result, error, arg) => [
        AUDIO_CHUNKS_TAG,
        { type: "AmbientScribes", id: arg.scribeId }
      ]
    }),

    // Delete an audio chunk (and its linked session). Use when chunk.id !== null.
    // For transcript-only chunks (id === null), use deleteScribeRecordingSession.
    deleteScribeAudioChunk: builder.mutation<
      void,
      { scribeId: number; chunkId: number }
    >({
      query: ({ scribeId, chunkId }) => ({
        url: `/ai/ambient-scribes/${scribeId}/audio-chunks/${chunkId}/`,
        method: "DELETE"
      }),
      invalidatesTags: (result, error, arg) => [
        AUDIO_CHUNKS_TAG,
        { type: "AmbientScribes", id: arg.scribeId },
        "AmbientScribes",
        "AmbientScribeProcessingEvents"
      ]
    }),

    // Ask-AI on the custom context. Mirrors POST /chart-notes/{id}/complete →
    // returns an HTML string. Transient (no cache tags).
    askAiOnScribeContext: builder.mutation<
      string,
      {
        scribe_id: number;
        text: string;
        user_prompt: string;
        clinic_id?: number;
      }
    >({
      query: ({ scribe_id, ...body }) => ({
        url: `/ai/ambient-scribes/${scribe_id}/context/complete/`,
        method: "POST",
        body
      })
    }),

    getAmbientScribeTemplates: builder.query<
      AmbientScribeTemplate[],
      AmbientScribeTemplateFilters
    >({
      query: (filters) => {
        // `type` and `query` are undefined unless the caller is behind
        // AMBIENT_SCRIBE_TEMPLATE_TYPES; the formatter drops undefined keys, so the
        // flag-off URL is byte-identical to the pre-SN-19067 one.
        const params: Record<string, string | number | undefined> = {
          limit: filters.limit ?? 100,
          offset: filters.offset ?? 0,
          ordering: filters.ordering ?? "-pk",
          search: filters.search,
          type: filters.type,
          query: filters.query
        };
        const queryString = formatValidQueryParamUrlCommaSeparated(params);
        return {
          url: `/ai/ambient-scribes/templates/${queryString ? `?${queryString}` : ""}`,
          method: "GET"
        };
      },
      extraOptions: { maxRetries: 1 },
      providesTags: [AMBIENT_SCRIBE_TEMPLATES_TAG],
      transformResponse: (
        response:
          | AmbientScribeTemplate[]
          | { results: AmbientScribeTemplate[]; count?: number }
      ) => {
        if (Array.isArray(response)) {
          return response;
        }
        return response.results ?? [];
      }
    }),

    getAmbientScribeTemplate: builder.query<AmbientScribeTemplate, number>({
      query: (id) => ({
        url: `/ai/ambient-scribes/templates/${id}/`,
        method: "GET"
      }),
      providesTags: (_result, _error, id) => [
        { type: AMBIENT_SCRIBE_TEMPLATES_TAG, id }
      ]
    }),

    createAmbientScribeTemplate: builder.mutation<
      AmbientScribeTemplate,
      AmbientScribeTemplateCreateInput
    >({
      query: (body) => ({
        url: "/ai/ambient-scribes/templates/",
        method: "POST",
        body
      }),
      invalidatesTags: [AMBIENT_SCRIBE_TEMPLATES_TAG]
    }),

    updateAmbientScribeTemplate: builder.mutation<
      AmbientScribeTemplate,
      AmbientScribeTemplateUpdateInput & { id: number }
    >({
      query: ({ id, ...body }) => ({
        url: `/ai/ambient-scribes/templates/${id}/`,
        method: "PATCH",
        body
      }),
      invalidatesTags: [AMBIENT_SCRIBE_TEMPLATES_TAG]
    }),

    deleteAmbientScribeTemplate: builder.mutation<void, number>({
      query: (id) => ({
        url: `/ai/ambient-scribes/templates/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: [AMBIENT_SCRIBE_TEMPLATES_TAG]
    }),

    reverseEngineerAmbientScribeTemplate: builder.mutation<
      { template: string },
      { text: string; format?: PromptOutputFormat }
    >({
      query: (body) => ({
        url: "/ai/ambient-scribes/templates/reverse-engineering/",
        method: "POST",
        body
      })
    }),

    // Kick off async AI template drafting. Returns { status: "processing", task_id }
    // (or a terminal status if the BE resolves synchronously). Transient — no cache tags.
    generateScribeTemplateAsync: builder.mutation<
      ScribeTemplateGenerationStatus,
      GenerateScribeTemplateInput
    >({
      query: (body) => ({
        url: "/ai/ambient-scribes/templates/generate/",
        method: "POST",
        body: { format: "html", ...body }
      })
    }),

    // Poll the generation task by id. Transient (no cache tags); GET auto-retries
    // transient timeouts via the services.util retry wrapper.
    //
    // keepUnusedDataFor: 0 is load-bearing — this is a live "current state of THIS
    // task" read, not cacheable data. If the BE ever returns a task_id we have
    // already polled to completion, a retained cache entry would be served
    // synchronously as `success` with the OLD result, so the poll would terminate
    // on stale content and never fetch the new draft. Dropping the entry the moment
    // polling unsubscribes guarantees each generation starts from a fresh fetch.
    getScribeTemplateGenerationStatus: builder.query<
      ScribeTemplateGenerationStatus,
      { task_id: string }
    >({
      query: ({ task_id }) => ({
        url: `/ai/ambient-scribes/templates/generate/${task_id}/`
      }),
      keepUnusedDataFor: 0,
      extraOptions: { maxRetries: 1 }
    }),

    /**
     * The recording as an MP3 the server converts on demand (SN-19937). Polled by its caller, so no
     * cache and no tags; the caller shows its own errors.
     */
    getScribeAudioExport: builder.query<ScribeAudioExport, { id: number }>({
      query: ({ id }) => ({
        url: `/ai/ambient-scribes/${id}/audio/`,
        params: { audio_format: "mp3" }
      }),
      keepUnusedDataFor: 0,
      extraOptions: { suppressGlobalErrorToast: true, maxRetries: 1 }
    }),

    getAmbientScribeProcessingContext: builder.query<
      AmbientScribeProcessingContext,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/ai/ambient-scribes/${id}/processing-context/`
      }),
      transformResponse: (response: AmbientScribeProcessingContextApi) =>
        transformAmbientScribeProcessingContextResponse(response),
      providesTags: (result, error, arg) => [
        { type: "AmbientScribes", id: arg.id },
        "AmbientScribeProcessingEvents"
      ]
    }),

    getProcessingEvents: builder.query<
      GetProcessingEventsResponse,
      GetProcessingEventsQueryArg
    >({
      query: ({
        scribeId,
        offset = 0,
        limit = 25
      }: GetProcessingEventsQueryArg) => ({
        url: `/ai/ambient-scribes/${scribeId}/processing-events/?offset=${offset}&limit=${limit}`
      }),
      transformResponse: (
        response: GetProcessingEventsApiPayload,
        _meta: unknown,
        arg: GetProcessingEventsQueryArg
      ) => normalizeProcessingEventsResponse(response, arg),
      providesTags: ["AmbientScribeProcessingEvents"]
    })
  })
});

export const {
  useGetAmbientScribeQuery,
  useGetAmbientScribeBackendStatusQuery,
  useLazyGetAmbientScribeBackendStatusQuery,
  useGetAmbientScribesQuery,
  useGetAmbientScribesPaginatedQuery,
  useGetAmbientScribeFeedbackQuery,
  useCreateAmbientScribeMutation,
  useUpdateAmbientScribeMutation,
  useDeleteAmbientScribeMutation,
  useGetAmbientScribesForMergeQuery,
  useCheckAmbientScribesMergeMutation,
  useMergeAmbientScribesMutation,
  useProbeAmbientScribesExistMutation,
  useGenerateAmbientSummaryMutation,
  useGenerateAmbientSoapNotesMutation,
  useGenerateAmbientScribeAsyncMutation,
  useGenerateAudiologyReportSummaryMutation,
  useGenerateAfterVisitSummaryMutation,
  useGenerateDebriefMutation,
  useGetAmbientScribeNotesQuery,
  useUpdateAmbientScribeNoteMutation,
  useGenerateTranscriptionMutation,
  useSubmitAmbientScribeFeedbackMutation,
  useFinalizeStuckScribeMutation,
  useExtractInsightsMutation,
  usePauseAmbientScribeMutation,
  useSyncAmbientScribeContextMutation,
  useGetScribeRecordingSessionsQuery,
  useDeleteScribeRecordingSessionMutation,
  useEditAmbientScribeMutation,
  useCompleteAmbientScribeMutation,
  useGetScribeAudioChunksQuery,
  useDeleteScribeAudioChunkMutation,
  useAskAiOnScribeContextMutation,
  useGetAmbientScribeTemplatesQuery,
  useGetAmbientScribeTemplateQuery,
  useCreateAmbientScribeTemplateMutation,
  useUpdateAmbientScribeTemplateMutation,
  useDeleteAmbientScribeTemplateMutation,
  useReverseEngineerAmbientScribeTemplateMutation,
  useGenerateScribeTemplateAsyncMutation,
  useGetScribeTemplateGenerationStatusQuery,
  useGetAmbientScribeProcessingContextQuery,
  useGetProcessingEventsQuery
} = ambientScribeApi;
